/**
 * Browser side of the shared template gallery.
 *
 * The API sends summaries for the list (a plain-text thumbnail, never the full
 * cell runs) and the full record only when someone chooses a template. Both
 * responses are treated as untrusted and pass through the same sanitizer used
 * for local template files before any grid or variable is created.
 */

import { api } from '../auth/api';
import { CellRun, encodeComponentRuns } from '../project/Serializer';
import type { VariableDef } from '../runtime/Variables';
import {
  UserTemplate,
  sanitizeUserTemplate,
  templateGrid,
} from './userTemplates';

export const PUBLIC_TEMPLATE_FORMAT = 'tui-template';
export const PUBLIC_TEMPLATE_VERSION = 1;

export type PublicTemplateSummary = {
  id: string;
  name: string;
  description: string;
  width: number;
  height: number;
  cells: number;
  bytes: number;
  preview: string;
  author: string;
  mine: boolean;
  createdAt: string;
};

export type PublicTemplateDetail = PublicTemplateSummary & {
  runs: CellRun[];
  variables: VariableDef[];
};

export type PublicTemplatePage = {
  templates: PublicTemplateSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type PublicTemplatePayload = {
  format: typeof PUBLIC_TEMPLATE_FORMAT;
  version: typeof PUBLIC_TEMPLATE_VERSION;
  name: string;
  description: string;
  width: number;
  height: number;
  runs: CellRun[];
  variables: VariableDef[];
};

export async function listPublicTemplates(options: {
  query?: string;
  offset?: number;
  limit?: number;
} = {}): Promise<PublicTemplatePage> {
  const params = new URLSearchParams();
  const query = options.query?.trim();
  if (query) params.set('q', query.slice(0, 50));
  params.set('offset', String(Math.max(0, Math.trunc(options.offset ?? 0))));
  params.set('limit', String(Math.max(1, Math.min(50, Math.trunc(options.limit ?? 8)))));
  return api<PublicTemplatePage>(`/api/templates?${params.toString()}`);
}

export async function getPublicTemplate(id: string): Promise<{
  summary: PublicTemplateSummary;
  template: UserTemplate;
}> {
  const result = await api<{ template: PublicTemplateDetail }>(
    `/api/templates/${encodeURIComponent(id)}`,
  );
  const template = sanitizeUserTemplate(result.template);
  if (!template) {
    throw new Error('The server returned a template that failed validation.');
  }
  return { summary: result.template, template };
}

/**
 * Canonicalises a local template into the wire format. Re-encoding the grid
 * guarantees the run lengths cover exactly `width × height`; the server
 * rejects anything else, so a malformed local record cannot become a broken
 * public one.
 */
export function publicTemplatePayload(
  template: UserTemplate,
  meta: { name: string; description: string },
): PublicTemplatePayload {
  const safe = sanitizeUserTemplate({ ...template, ...meta });
  if (!safe) throw new Error('Template failed validation before publishing.');
  const grid = templateGrid(safe);
  return {
    format: PUBLIC_TEMPLATE_FORMAT,
    version: PUBLIC_TEMPLATE_VERSION,
    name: safe.name,
    description: safe.description,
    width: grid.width,
    height: grid.height,
    runs: encodeComponentRuns((i) => grid.getAt(i), grid.length),
    variables: safe.variables,
  };
}

export async function publishPublicTemplate(
  template: UserTemplate,
  meta: { name: string; description: string },
): Promise<PublicTemplateSummary> {
  const result = await api<{ template: PublicTemplateSummary }>('/api/templates', {
    method: 'POST',
    body: publicTemplatePayload(template, meta),
  });
  return result.template;
}

export async function deletePublicTemplate(id: string): Promise<void> {
  await api<{ ok: boolean }>(`/api/templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
